/**
 * Supplier multi-select filter for the Supplier Purchase Review report.
 *
 * Thin wrapper over the shared `SearchSelect` (multi mode). Supplier names are
 * loaded once from the report's suppliers endpoint and filtered client-side
 * (the network set is ~1.3k names). Value is stored comma-joined to match the
 * other multi-value filters (e.g. SyncStatusField).
 */
import React, { useEffect, useState } from 'react'
import { apiClient } from '../../../../services/api'
import SearchSelect from '../../inputs/SearchSelect'

interface SupplierOption {
  name: string
  in_master: boolean
}

interface SupplierMultiSelectFieldProps {
  value: string // comma-joined supplier names
  onChange: (value: string) => void
  label?: string
  market?: string
  disabled?: boolean
}

const SupplierMultiSelectField: React.FC<SupplierMultiSelectFieldProps> = ({
  value,
  onChange,
  label = 'Suppliers',
  market,
  disabled = false,
}) => {
  const [options, setOptions] = useState<SupplierOption[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const params = new URLSearchParams()
    if (market) params.set('market', market)
    setLoading(true)
    apiClient
      .get(`/admin/reports/supplier-purchase-review/suppliers?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setOptions(res.data?.suppliers || [])
      })
      .catch((err) => {
        console.error('Failed to load suppliers:', err)
        if (!cancelled) setOptions([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [market])

  const selectedNames = value ? value.split(',').filter(Boolean) : []
  const selected = options.filter((o) => selectedNames.includes(o.name))

  return (
    <SearchSelect<SupplierOption>
      label={label}
      multiple
      dense
      disabled={disabled}
      loading={loading}
      options={options}
      value={selected}
      onChange={(next: SupplierOption[]) =>
        onChange(next.map((o) => o.name).join(','))
      }
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(o, v) => o.name === v.name}
      placeholder={selectedNames.length ? '' : 'Search suppliers...'}
      autocompleteProps={{ limitTags: 3 }}
    />
  )
}

export default SupplierMultiSelectField
